import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Firestore, doc, getDoc, DocumentSnapshot } from '@angular/fire/firestore';
import { Timestamp } from '@angular/fire/firestore';
import { of, switchMap } from 'rxjs';
import { Residencial } from '../models/residencial.model';
import { Usuario } from '../models/usuario.model';
import { Residente } from '../models/residente.model';
import { Guardia } from '../models/guardia.model';
import { Cuenta } from '../models/cuenta.model';
import { Servicio } from '../models/servicio.model';
import { Espacio } from '../models/espacio.model';
import { Bloqueo } from '../models/bloqueo.model';
import { Reserva } from '../models/reserva.model';
import { ResidenteService } from './residente.service';
import { GuardiaService } from './guardia.service';
import { CuentaService } from './cuenta.service';
import { ServicioService } from './servicio.service';
import { EspacioService } from './espacio.service';
import { BloqueoService } from './bloqueo.service';
import { ReservaService } from './reserva.service';

const LS_KEY = 'rc_admin_state';

interface AdminState {
  residencial: Residencial;
  adminUser:   Usuario;
}

// Versión serializable para JSON (Timestamp → { seconds, nanoseconds })
interface SerializedTimestamp { seconds: number; nanoseconds: number; }
type SerializedResidencial = Omit<Residencial, 'creadoEn' | 'suspendidoEn'> & {
  creadoEn:      SerializedTimestamp;
  suspendidoEn?: SerializedTimestamp;
};
type SerializedUsuario = Omit<Usuario, 'creadoEn'> & {
  creadoEn: SerializedTimestamp;
};

/**
 * Estado global del admin durante su sesión dentro de un tenant.
 *
 * Responsabilidades:
 * - Mantener el perfil del admin y su residencial en signals reactivos.
 * - Exponer los 5 catálogos del tenant (residentes, guardias, cuentas, servicios, espacios)
 *   como listeners en tiempo real — un solo listener por colección, compartido por
 *   todos los módulos que lo lean.
 * - Persistir el perfil en localStorage para sobrevivir recargas sin releer Firestore.
 *
 * Ciclo de vida de los listeners de catálogos:
 *   login  → loadForAdmin() → _residencial != null → _residencialId = 'abc123'
 *          → switchMap activa collectionData() para cada catálogo → 4 listeners abiertos
 *   logout → clearState() → _residencial = null → _residencialId = ''
 *          → switchMap emite of([]) → 5 listeners cancelados automáticamente
 *
 * Por qué centralizar aquí y no en cada página:
 *   Cada llamada a collectionData() abre un nuevo listener. Si cada página hiciera
 *   su propio getAll(), habría N listeners activos simultáneamente con lecturas
 *   duplicadas. Con este servicio hay exactamente 1 listener por colección.
 */
@Injectable({ providedIn: 'root' })
export class ResidencialStateService {
  private firestore    = inject(Firestore);
  private residenteSvc = inject(ResidenteService);
  private guardiaSvc   = inject(GuardiaService);
  private cuentaSvc    = inject(CuentaService);
  private servicioSvc  = inject(ServicioService);
  private espacioSvc   = inject(EspacioService);
  private bloqueoSvc   = inject(BloqueoService);
  private reservaSvc   = inject(ReservaService);

  private _residencial = signal<Residencial | null>(null);
  private _adminUser   = signal<Usuario | null>(null);

  readonly residencial = this._residencial.asReadonly();
  readonly adminUser   = this._adminUser.asReadonly();

  // _residencialId como computed garantiza que el string sea reactivo:
  // cuando _residencial cambia, _residencialId$ emite y switchMap reacciona.
  private readonly _residencialId  = computed(() => this._residencial()?.id ?? '');
  private readonly _residencialId$ = toObservable(this._residencialId);

  // Un solo listener por colección para toda la sesión del admin.
  // switchMap cancela el listener anterior si cambia el tenant (edge case: admin
  // de dos residenciales en el futuro) y emite of([]) al hacer logout (id = '').
  readonly residentes = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.residenteSvc.getAll(id) : of([]))),
    { initialValue: [] as Residente[] }
  );
  readonly guardias = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.guardiaSvc.getAll(id) : of([]))),
    { initialValue: [] as Guardia[] }
  );
  readonly cuentas = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.cuentaSvc.getAll(id) : of([]))),
    { initialValue: [] as Cuenta[] }
  );
  readonly servicios = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.servicioSvc.getAll(id) : of([]))),
    { initialValue: [] as Servicio[] }
  );
  readonly espacios = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.espacioSvc.getAll(id) : of([]))),
    { initialValue: [] as Espacio[] }
  );
  readonly bloqueos = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.bloqueoSvc.getAll(id) : of([]))),
    { initialValue: [] as Bloqueo[] }
  );
  readonly reservas = toSignal(
    this._residencialId$.pipe(switchMap(id => id ? this.reservaSvc.getReservas(id) : of([]))),
    { initialValue: [] as Reserva[] }
  );

  constructor() {
    // Rehidratar antes de que cualquier componente lea los signals —
    // permite que los listeners arranquen con el id correcto desde el primer ciclo.
    this.hydrateFromStorage();
  }

  /**
   * Carga el admin y su residencial desde Firestore y activa los 4 listeners de catálogos.
   * Debe llamarse en AuthService.login() antes de navegar al módulo /admin.
   */
  async loadForAdmin(uid: string, userSnap: DocumentSnapshot): Promise<void> {
    if (!userSnap.exists()) throw new Error('Usuario admin no encontrado en Firestore');

    const adminUser = { id: userSnap.id, ...userSnap.data() } as Usuario;

    const resSnap = await getDoc(doc(this.firestore, `residenciales/${adminUser.residencialId}`));
    if (!resSnap.exists()) throw new Error('Residencial no encontrada en Firestore');

    const residencial = { id: resSnap.id, ...resSnap.data() } as Residencial;

    this._residencial.set(residencial);
    this._adminUser.set(adminUser);
    this.saveToStorage({ residencial, adminUser });
  }

  /**
   * Limpia estado en memoria y localStorage.
   * _residencialId pasa a '' → switchMap emite of([]) → 4 listeners cancelados.
   * Debe llamarse en AuthService.logout() antes de signOut().
   */
  clearState(): void {
    this._residencial.set(null);
    this._adminUser.set(null);
    localStorage.removeItem(LS_KEY);
  }

  // ── Persistencia ────────────────────────────────────────────────────────────

  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as {
        residencial: SerializedResidencial;
        adminUser:   SerializedUsuario;
      };

      this._residencial.set(this.deserializeResidencial(parsed.residencial));
      this._adminUser.set(this.deserializeUsuario(parsed.adminUser));
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }

  private saveToStorage(state: AdminState): void {
    const serialized = {
      residencial: this.serializeResidencial(state.residencial),
      adminUser:   this.serializeUsuario(state.adminUser)
    };
    localStorage.setItem(LS_KEY, JSON.stringify(serialized));
  }

  // ── Serialización de Timestamps ─────────────────────────────────────────────
  // Firestore Timestamp no es JSON-serializable directamente.
  // Se guarda como { seconds, nanoseconds } y se reconstruye al leer.

  private serializeResidencial(r: Residencial): SerializedResidencial {
    return {
      ...r,
      creadoEn:     { seconds: r.creadoEn.seconds, nanoseconds: r.creadoEn.nanoseconds },
      suspendidoEn: r.suspendidoEn
        ? { seconds: r.suspendidoEn.seconds, nanoseconds: r.suspendidoEn.nanoseconds }
        : undefined
    };
  }

  private deserializeResidencial(raw: SerializedResidencial): Residencial {
    return {
      ...raw,
      creadoEn:     new Timestamp(raw.creadoEn.seconds, raw.creadoEn.nanoseconds),
      suspendidoEn: raw.suspendidoEn
        ? new Timestamp(raw.suspendidoEn.seconds, raw.suspendidoEn.nanoseconds)
        : undefined
    };
  }

  private serializeUsuario(u: Usuario): SerializedUsuario {
    return {
      ...u,
      creadoEn: { seconds: u.creadoEn.seconds, nanoseconds: u.creadoEn.nanoseconds }
    };
  }

  private deserializeUsuario(raw: SerializedUsuario): Usuario {
    return {
      ...raw,
      creadoEn: new Timestamp(raw.creadoEn.seconds, raw.creadoEn.nanoseconds)
    };
  }
}
